<template>
	<div :class="[`landing_background_${background}`, `landing_theme_${theme}`]" class="landing">
		<div v-if="stepLayout" class="landing__layout container-fluid">
			<template v-for="(layoutItem, index) in stepLayout" :key="index">
				<component
					:is="layoutItem.component"
					v-bind="layoutComponentsProps[layoutItem.component]"
					v-on="layoutItem.handlers"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import { mapActions, mapState } from 'pinia'
import { useMainStore } from '@/store/main'
import { useI18nStore } from '@/store/i18n'
import { useLocationStore } from '@/store/location'
import { useSurveyStore } from '@/store/survey'
import { useUserFlagsStore } from '@/store/userFlags'
import { useCookieConsentStore } from '@/store/cookieConsent'

import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { matchOverride } from '@/helpers/jsonConfig'
import { mergeDeep } from '@/helpers/utils'

import { getAppAreaExperimentPart } from '@/funnels/funnels.ts'
import analytics, { eventInt } from '@/modules/analytics'
import { treatment } from '@/modules/abPlatform'
import { STEP_TYPE, useOnboardingStepEvents } from '@/composables/useOnboardingStepEvents'
import * as Sentry from '@sentry/vue'

import landingComponentsList from '@/modules/Landing/components'
import { instances } from '@/modules/Landing/landingInstances.ts'

const componentsMap = landingComponentsList.reduce((componentsMap, component) => {
	componentsMap[component.name] = component
	return componentsMap
}, {})

const emitsMap = landingComponentsList.reduce((emitsMap, component) => {
	emitsMap[component.name] = component.emits ?? []
	return emitsMap
}, {})

// Maps each landing CTA's entry point to the id of the block it lives in, so
// OnboardingButtonClick.block_name reflects where the user clicked (and joins to the
// matching OnboardingBlockView). `weight_loss_question_answer` is a quiz answer (flows
// through StepDone data), not a CTA, so it is intentionally absent.
//
// `header_eligible` is absent for a different reason: per AL-12446 #99131 the header is
// not a tracked block, so nothing emits OnboardingBlockView for it and a block_name here
// would only produce clicks with nothing to join to. The header CTA still advances the
// funnel — it just carries no block attribution.
const LANDING_CTA_BLOCK_NAMES = {
	main_eligible: 'MAIN',
	calculator_eligible: 'CALCULATOR',
	how_it_works_eligible: 'HOW_IT_WORKS',
	in_stock_eligible: 'IN_STOCK',
	final_cta_eligible: 'FINAL_CTA',
	why_kova_eligible: 'WHY_KOVA',
	everything_you_need_right_for_me: 'EVERYTHING_YOU_NEED',
	how_it_works_right_for_me: 'HOW_IT_WORKS',
	companion_app_eligible: 'COMPANION_APP',
	body_signals_eligible: 'BODY_SIGNALS',
	medication_limits_eligible: 'MEDICATION_LIMITS',
	faq_eligible: 'FAQ',
}

export default {
	name: 'LandingView',

	components: componentsMap,

	setup() {
		const { stepEvents, jetStepShow, jetStepDone, jetButtonClick } = useOnboardingStepEvents()
		return { stepEvents, jetStepShow, jetStepDone, jetButtonClick }
	},

	data() {
		return {
			step: 0,
			schemas: [],
			isCreativeOverride: false,
			handlersMap: {
				progressLoaderStarted: this.onProgressLoaderStarted,
				progressLoaderCompleted: this.onProgressLoaderCompleted,
			},
		}
	},

	computed: {
		...mapState(useI18nStore, {
			language: 'getLanguage',
			customLanding: 'getCustomLanding',
		}),

		...mapState(useMainStore, {
			countryCode: 'getCountryCode',
		}),

		...mapState(useLocationStore, {
			appAreaConfig: 'getAppAreaConfig',
			getQueryParam: 'getQueryParam',
		}),

		currentStepSchema() {
			return this.schemas[this.step]
		},

		background() {
			return this.currentStepSchema?.config?.background ?? 'base'
		},

		theme() {
			return this.currentStepSchema?.config?.theme ?? 'default'
		},

		stepLayout() {
			return this.currentStepSchema?.order.map((layoutComponentName) => {
				return {
					component: layoutComponentName,
					handlers: this.getComponentHandlersMapByComponentName(layoutComponentName),
				}
			})
		},

		layoutComponentsProps() {
			const ctxParams = {
				language: this.language,
			}

			if (this.currentStepSchema.config?.dynamicLandingType) {
				ctxParams.dynamicLandingData = this.getDynamicLandingData(
					this.currentStepSchema.config.dynamicLandingType,
					this.getQueryParam('lpid'),
				)
			}

			return this.stepLayout.reduce((props, layoutComponent) => {
				const layoutComponentName = layoutComponent.component

				let propsGenerator = this.currentStepSchema.components[layoutComponentName]

				if (typeof propsGenerator === 'function') {
					props[layoutComponentName] = propsGenerator({
						ctx: ctxParams,
						$t: this.$t.bind(this),
						$tc: this.$tc.bind(this),
						$n: this.$n.bind(this),
						$te: this.$te.bind(this),
						$tp: this.$tp.bind(this),
					})
				}

				return props
			}, {})
		},
	},

	watch: {
		'$route.query.step': {
			handler(newStep) {
				const stepNumber = parseInt(newStep) || 0
				if (stepNumber !== this.step) {
					this.step = stepNumber
					if (this.schemas.length) {
						this.trackAnalyticsShowScreenEvents()
					}
				}
			},

			immediate: true,
		},
	},

	async created() {
		const appAreaExperimentLandingPart = getAppAreaExperimentPart(this.appAreaConfig.appArea, 'landing')
		let landingIds = this.getCurrentLandingIds()
		if (appAreaExperimentLandingPart) {
			if (Array.isArray(appAreaExperimentLandingPart.ids)) {
				landingIds = appAreaExperimentLandingPart.ids
			}
		}
		this._landingIds = landingIds

		if (!landingIds || landingIds.length === 0) {
			Sentry.captureMessage('Landing ids not found')
			return this.$router.push({ name: 'landing' })
		}

		let schemas = landingIds
			.map((id) => {
				const configModule = instances[id]
				if (!configModule) {
					Sentry.captureMessage(`Landing config not found for id: ${id}`)
					return null
				}
				return Object.freeze(configModule)
			})
			.filter(Boolean)

		if (appAreaExperimentLandingPart?.experiment) {
			const experimentModule = await appAreaExperimentLandingPart.experiment()
			const experimentalConfig = experimentModule.default(schemas)

			schemas = experimentalConfig
		}

		this.schemas = schemas

		if (this.schemas.length === 0) {
			Sentry.captureMessage('No valid landing schemas found')
			return this.$router.push({ name: 'landing' })
		}

		this.updateLocalFlags({ funnelFeatureFlag: this.currentStepSchema?.config?.funnelFeatureFlag ?? null })

		// Loader-style landings hide the Cookiebot banner while on screen so it doesn't
		// cover the interstitial; released in beforeUnmount as we hand off to the funnel,
		// where the banner should surface (the first question screen).
		if (this.currentStepSchema?.config?.deferCookieBanner) {
			this.deferBanner()
		}

		useLocationStore().setInitialFunnelType(this.appAreaConfig.funnel.slug)
		useLocationStore().setInitialFunnelId(this.appAreaConfig.funnel.id)
		// Track experiment exposure for web_simple_web_app_intro
		this.resetAnalyticDataCounter()
		this.trackAnalyticsShowScreenEvents()
	},

	beforeUnmount() {
		// Reveal the banner again as we leave the landing (idempotent if we never deferred).
		this.releaseBanner()

		window.dataLayer?.push({
			event: 'updateGtagData',
			tracking_enabled: true,
		})
	},

	methods: {
		...mapActions(useSurveyStore, ['resetAnalyticDataCounter']),
		...mapActions(useUserFlagsStore, ['updateLocalFlags']),
		...mapActions(useCookieConsentStore, ['deferBanner', 'releaseBanner']),

		getCurrentLandingIds() {
			const locationStore = useLocationStore()

			const override = matchOverride(this.appAreaConfig.landing.overrides, {
				...locationStore.queryParams,
				language: this.language,
				country: this.countryCode,
			})

			// DEV-35776: креатив выбрал вариант — вместе с ним он назвал и препарат
			// (`landing.overrides[].medication`). Кладём его в стор здесь, в единственной
			// точке, где вариант вообще резолвится: дальше `survey.effectiveMedicationChoice`
			// возьмёт его оттуда, и пейвол не будет во второй раз разбирать `utm_term`.
			// Универсальный лендинг пишет null — креатив препарат не называл.
			locationStore.setLandingMedication(override?.medication)

			if (Array.isArray(override?.ids)) {
				this.isCreativeOverride = true
				return override.ids
			}

			return this.appAreaConfig.landing.ids
		},

		getComponentHandlersMapByComponentName(componentName) {
			const componentEventsList = emitsMap[componentName]
			return componentEventsList
				.filter((eventName) => typeof this.handlersMap[eventName] === 'function')
				.reduce((componentHandlersMap, eventName) => {
					componentHandlersMap[eventName] = this.handlersMap[eventName]
					return componentHandlersMap
				}, {})
		},

		getDynamicLandingData(dynamicLandingType, lpid) {
			const configDynamicLandingData = markRaw({ ...(this.customLanding.lpid[dynamicLandingType]?.[lpid] ?? {}) })

			if (ENV_DEV_OR_STAGING) {
				return mergeDeep(
					{ ...configDynamicLandingData },
					{
						title: this.$route.query.lp_title ?? configDynamicLandingData.title,
						subtitle: this.$route.query.lp_subtitle ?? configDynamicLandingData.subtitle,
						loader: {
							label: this.$route.query.lp_loader_label ?? configDynamicLandingData.loader?.label,
							note: this.$route.query.lp_loader_note ?? configDynamicLandingData.loader?.note,
						},
						feature_description:
							this.$route.query.lp_feature_description ?? configDynamicLandingData.feature_description,
						disclaimer: this.$route.query.lp_disclaimer ?? configDynamicLandingData.disclaimer,
						image_url: this.$route.query.lp_image_url ?? configDynamicLandingData.image_url,
						animation_url: this.$route.query.lp_animation_url ?? configDynamicLandingData.animation_url,
					},
				)
			}

			return configDynamicLandingData
		},

		setAmplitudeUserProperties() {
			// Analytics properties removed
			return Promise.resolve()
		},

		trackAnalyticsShowScreenEvents() {
			if (this.step === 0) {
				// Per spec §6.3: fired once per onboarding session per platform.  Current
				// behaviour mirrors the legacy Jet wiring (fires on every step-0 mount);
				// session-level idempotency is enforced by the analytics backend de-duping
				// on (instance_id, session_id) since the event has no payload to vary on.
				analytics.track('OnboardingStart', {})

				// DEV-35179: experiment exposure at first touch (= onboarding start).
				// Synchronous: first paint waits for the AB render gate (status store), so
				// the context is ready here — or failed/deadline-expired, in which case this
				// returns control with no exposure. Assigns the unit and emits the
				// ExperimentExposure analytics event; the variant is unused (A/A test),
				// repeats de-dupe downstream on (sessionId, experiment) like OnboardingStart.
				treatment('web_aa_onboarding_start')
			}

			const landingId = this._landingIds?.[this.step] || 'unknown'
			this.jetStepShow({ name: `LANDING_${landingId}`, stepType: STEP_TYPE.LANDING }, false, false)
		},

		trackAnalyticsDoneScreenEvents(entryPoint, dataString) {
			// jetStepDone now fires OnboardingStepDone via analytics.track.  For the
			// glp landing first step (a question — "What's your weight loss goal?"),
			// the answer is passed through `dataString` so it lands in event_properties
			// per AL-12446 #98664 point 2.
			this.jetStepDone(false, dataString)

			if (this.step === 0) {
				analytics.track('OnboardingWelcomeDone', {
					...(this.stepEvents.currentStepId && { onboarding_step_id: this.stepEvents.currentStepId }),
					...(this.stepEvents.lastSecondsOnScreen != null && {
						seconds_on_screen: eventInt(this.stepEvents.lastSecondsOnScreen),
					}),
					// entry_point — which CTA the user clicked to enter the funnel.
					// Canonical strings come from AL-12446 #99131: weight_loss_question_answer,
					// main_eligible, everything_you_need_right_for_me, how_it_works_right_for_me,
					// medication_limits_eligible, body_signals_eligible, faq_eligible, menu_eligible.
					...(entryPoint && { entry_point: entryPoint }),
				})
			}
		},

		goNext(entryPoint, dataString) {
			this.trackAnalyticsDoneScreenEvents(entryPoint, dataString)

			if (this.step < this.schemas.length - 1) {
				this.$router.push({
					query: {
						...this.$route.query,
						step: this.step + 1,
					},
				})
			} else {
				this.$router.push({ name: this.appAreaConfig.funnel.route.name })
			}
		},

		onProgressLoaderStarted() {},

		onProgressLoaderCompleted(entryPoint, dataString, visibleBlockName) {
			if (ENV_DEV_OR_STAGING && this.$route.query.loader_pause) {
				return
			}
			const blockName = visibleBlockName ?? LANDING_CTA_BLOCK_NAMES[entryPoint]
			if (blockName) {
				this.jetButtonClick('CHECK_MY_ELIGIBILITY', blockName)
			}
			this.goNext(entryPoint, dataString)
		},
	},
}
</script>

<style lang="scss" scoped>
.landing {
	&_background {
		&_base {
			background-color: var(--Background-Base);
		}

		&_accent {
			background-color: var(--Fill-Accent-Dimmed);
		}

		&_transparent {
			background-color: transparent;
		}

		&_brown {
			background-color: var(--Background-Brown);
		}

		&_kova {
			background-color: var(--Fill-Accent-100);
		}

		// The Kova blocks' page colour, so the root always matches the theme.
		&_page {
			background-color: var(--kova-lp-page);
		}
	}

	&__layout {
		// dvh keeps bottom-pinned landing content (kova loader bar) above mobile
		// browser chrome; vh fallback for pre-dvh browsers (same pattern as
		// FunnelContainer). For scrollable landings min-height isn't binding.
		min-height: 100vh;
		min-height: 100dvh;
		padding-bottom: 120px; // 104px cookie banner + 16px padding
		overflow: auto; // Fix to prevent the margin-top of the first element from extending beyond the screen

		&:has(.glp1),
		&:has(.kova-hero),
		&:has(.kova-loader) {
			padding-bottom: 0;
			overflow: hidden;
		}

		// The marketing landing is full-bleed — its blocks carry their own gutters,
		// so the .container-fluid padding has to go. Its 500px cap (style.scss) is
		// the funnel's mobile column and still applies below the desktop breakpoint;
		// the desktop composition is 1440 wide and needs the whole viewport.
		// LandingKovaHeroUsa and LandingKovaV2Hero bring their own headers, so they switch this on as well.
		&:has(.kova-lp-header, .kova-lp-usa, .kova-v2-hero) {
			padding-inline: 0;
			// The footer ends the page and already reserves 120px under its watermark,
			// so the layout's own cookie-banner gutter would only add dead space below
			// the dark panel.
			padding-bottom: 0;

			@media (min-width: 1024px) {
				max-width: none;
			}
		}
	}
}
</style>
